import { DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import { Button, Checkbox, Col, Row, Select, Typography } from 'antd';
import { useEffect, useState } from 'react';
import type { MatchRuleMode } from '@lens/shared';
import { getDictionary } from '../dictionaries/api.ts';

export interface MatchRuleFieldOption {
  slug: string;
  name: string;
  fieldType: string;
  config: unknown;
}

export interface MatchRuleRow {
  sourceField: string;
  targetField: string;
  mode: MatchRuleMode;
  required: boolean;
  lookupColumn?: string;
}

interface MatchRulesEditorProps {
  sourceFields: MatchRuleFieldOption[];
  targetFields: MatchRuleFieldOption[];
  value: MatchRuleRow[];
  onChange: (next: MatchRuleRow[]) => void;
}

const MATCH_MODE_LABELS: Record<MatchRuleMode, string> = {
  eq: 'Точное совпадение',
  any_in: 'Любой из массива (source → target)',
  in_any: 'Значение в массиве (target)',
  dict_lookup: 'Через справочник',
};

const { Text } = Typography;

async function getDictionaryColumns(dictionaryId: string): Promise<Array<{ slug: string; name: string }>> {
  const dictionary = await getDictionary(dictionaryId);
  return (dictionary.columns ?? []).map((column) => ({
    slug: column.slug,
    name: column.name,
  }));
}

function isNonComputedField(field: MatchRuleFieldOption): boolean {
  return field.fieldType !== 'computed';
}

export function MatchRulesEditor({
  sourceFields,
  targetFields,
  value,
  onChange,
}: MatchRulesEditorProps): JSX.Element {
  const [dictColsCache, setDictColsCache] = useState<
    Record<string, Array<{ slug: string; name: string }>>
  >({});

  useEffect(() => {
    setDictColsCache({});
  }, [targetFields]);

  const updateRow = (index: number, patch: Partial<MatchRuleRow>): void => {
    onChange(value.map((row, rowIndex) => (rowIndex === index ? { ...row, ...patch } : row)));
  };

  const handleLoadDictionaryColumns = async (rule: MatchRuleRow): Promise<void> => {
    const targetField = targetFields.find((field) => field.slug === rule.targetField);
    if (!targetField || targetField.fieldType !== 'reference' || dictColsCache[rule.targetField]) {
      return;
    }

    const config = targetField.config as { dictionaryId?: string } | null;
    if (!config?.dictionaryId) {
      return;
    }

    try {
      const columns = await getDictionaryColumns(config.dictionaryId);
      setDictColsCache((prev) => ({ ...prev, [rule.targetField]: columns }));
    } catch {
      // Ошибку покажет родитель при следующем действии; здесь не шумим.
    }
  };

  return (
    <>
      <div style={{ marginTop: 12, marginBottom: 4 }}>
        <Text strong style={{ fontSize: 13 }}>Правила совпадения</Text>{' '}
        <Text type="secondary" style={{ fontSize: 12 }}>
          — необязательно, без правил автосвязывание не сработает
        </Text>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 12 }}>
        {value.map((rule, index) => {
          const targetField = targetFields.find((field) => field.slug === rule.targetField);
          const isReferenceTarget = targetField?.fieldType === 'reference';
          const lookupOptions = (dictColsCache[rule.targetField] ?? []).map((column) => ({
            value: column.slug,
            label: `${column.name} (${column.slug})`,
          }));

          return (
            <div
              key={`${rule.sourceField}-${rule.targetField}-${index}`}
              style={{
                border: '1px solid #f0f0f0',
                borderRadius: 8,
                padding: '10px 12px',
                background: '#fafafa',
                position: 'relative',
              }}
            >
              <Button
                type="text"
                danger
                size="small"
                icon={<DeleteOutlined />}
                style={{ position: 'absolute', top: 6, right: 6 }}
                onClick={() => onChange(value.filter((_, rowIndex) => rowIndex !== index))}
              />

              <Row gutter={12}>
                <Col span={11}>
                  <Text type="secondary" style={{ fontSize: 11 }}>Поле источника</Text>
                  <Select
                    style={{ width: '100%', marginTop: 2 }}
                    placeholder="Выберите поле"
                    value={rule.sourceField || undefined}
                    onChange={(nextValue) => updateRow(index, { sourceField: nextValue })}
                    options={sourceFields.filter(isNonComputedField).map((field) => ({
                      value: field.slug,
                      label: `${field.name} (${field.slug})`,
                    }))}
                  />
                </Col>
                <Col span={11}>
                  <Text type="secondary" style={{ fontSize: 11 }}>Поле цели</Text>
                  <Select
                    style={{ width: '100%', marginTop: 2 }}
                    placeholder="Выберите поле"
                    value={rule.targetField || undefined}
                    onChange={(nextValue) =>
                      updateRow(index, { targetField: nextValue, lookupColumn: undefined })
                    }
                    options={targetFields.filter(isNonComputedField).map((field) => ({
                      value: field.slug,
                      label: `${field.name} (${field.slug})`,
                    }))}
                  />
                </Col>
              </Row>

              <Row gutter={12} style={{ marginTop: 8 }}>
                <Col span={16}>
                  <Text type="secondary" style={{ fontSize: 11 }}>Режим сравнения</Text>
                  <Select
                    style={{ width: '100%', marginTop: 2 }}
                    value={rule.mode}
                    onChange={(nextValue) =>
                      updateRow(index, {
                        mode: nextValue as MatchRuleMode,
                        lookupColumn: nextValue === 'dict_lookup' ? rule.lookupColumn : undefined,
                      })
                    }
                    options={Object.entries(MATCH_MODE_LABELS).map(([mode, label]) => ({
                      value: mode,
                      label,
                    }))}
                  />
                </Col>
                <Col span={8} style={{ display: 'flex', alignItems: 'flex-end', paddingBottom: 4 }}>
                  <Checkbox
                    checked={rule.required}
                    onChange={(event) => updateRow(index, { required: event.target.checked })}
                  >
                    Обязательное
                  </Checkbox>
                </Col>
              </Row>

              {rule.mode === 'dict_lookup' && (
                <Row gutter={12} style={{ marginTop: 8 }}>
                  <Col span={16}>
                    <Text type="secondary" style={{ fontSize: 11 }}>
                      Колонка в справочнике (по полю цели)
                    </Text>
                    <Select
                      style={{ width: '100%', marginTop: 2 }}
                      placeholder="Выберите колонку"
                      value={rule.lookupColumn || undefined}
                      allowClear
                      showSearch
                      onFocus={() => {
                        void handleLoadDictionaryColumns(rule);
                      }}
                      options={lookupOptions}
                      notFoundContent={
                        isReferenceTarget
                          ? (dictColsCache[rule.targetField] ? 'Нет колонок' : 'Загрузка...')
                          : 'Поле не ссылается на справочник'
                      }
                      onChange={(nextValue) =>
                        updateRow(index, { lookupColumn: nextValue ?? undefined })
                      }
                    />
                  </Col>
                </Row>
              )}
            </div>
          );
        })}
      </div>

      <Button
        type="dashed"
        icon={<PlusOutlined />}
        style={{ width: '100%' }}
        onClick={() =>
          onChange([
            ...value,
            {
              sourceField: '',
              targetField: '',
              mode: 'eq',
              required: false,
              lookupColumn: undefined,
            },
          ])
        }
      >
        Добавить правило
      </Button>
    </>
  );
}
